/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, memo, useEffect } from 'react'
import { reaction } from 'mobx'
import { useFormState } from 'react-final-form'
import { Link, Typography } from '@mui/material'

import { progressiveDisclosureValidator, VALIDATOR_EMAIL } from 'validation'
import ProgressiveDisclosed from 'components/ProgressiveDisclosed'
import ConditionalWrapper from 'components/ConditionalWrapper'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import { TrackEventData } from '../../../utils/tracking/tracking.interface'
import { pushGTMEvent } from '../../../utils/tracking'
import PathCheckboxYesNo from '../../PathCheckboxYesNo'
import { useStores } from '../../../stores/utils/hooks/useStores'

interface LabelProps {
  declarationOfConsentAdvertising?: string
}

const Label: FunctionComponent<LabelProps> = memo(
  ({ declarationOfConsentAdvertising = '' }) => (
    <Typography variant="body2">
      Ich möchte zudem über günstige Kreditangebote und Services von smava
      informiert werden und willige ein, dass smava meine Angaben zur
      Kontaktaufnahme nutzt. Ich kann meine{' '}
      <Link
        href={declarationOfConsentAdvertising}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
      >
        freiwillige Einwilligung
      </Link>{' '}
      jederzeit mit Wirkung für die Zukunft widerrufen.
    </Typography>
  )
)

Label.displayName = 'Label'

const Agb = ({
  dependson,
  ctaButtonLabel = 'Weiter',
  disableDisclose
}: {
  dependson?: string
  ctaButtonLabel?: string
  disableDisclose?: boolean
}) => {
  const { traversal } = useStores()
  const clientDetails = useClientDetails()

  const { values } = useFormState()

  const primaryDebtorData = values.debtors.primary

  useEffect(() => {
    reaction(
      () => traversal.data.common.dataProcessingConsent,
      () => {
        const event: {
          'contact-data-processing-consent': number
        } & TrackEventData = {
          event: 'wsContactDataProcessingConsentCheck',
          'contact-data-processing-consent': Number(
            traversal.data.common.dataProcessingConsent
          )
        }

        pushGTMEvent(event)
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ProgressiveDisclosed
      value={primaryDebtorData?.contact?.email}
      validator={progressiveDisclosureValidator(VALIDATOR_EMAIL)}
      disableDisclose={disableDisclose}
    >
      <ConditionalWrapper dependson={dependson}>
        <>
          <Typography variant="body2">
            {`Mit Klick auf den "${ctaButtonLabel}"-Button akzeptiere ich die`}{' '}
            <Link
              href={clientDetails?.urls?.termsAndConditions}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              AGB
            </Link>{' '}
            und erteile smava einen kostenlosen Kreditvergleichs- und
            Vermittlungsauftrag. Ich habe die{' '}
            <Link
              href={clientDetails?.urls?.precontractual}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Pflichtinformationen
            </Link>{' '}
            und die{' '}
            <Link
              href={clientDetails?.urls?.dataProtection}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Datenschutzhinweise
            </Link>{' '}
            erhalten.
          </Typography>
          <PathCheckboxYesNo
            alignOnTop
            path="common.dataProcessingConsent"
            label={
              <Label
                declarationOfConsentAdvertising={
                  clientDetails?.urls?.declarationOfConsentAdvertising
                }
              />
            }
          />
        </>
      </ConditionalWrapper>
    </ProgressiveDisclosed>
  )
}

export default memo(Agb)
